import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  CircularProgress,
  Grid,
  Paper,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { PDFDocument, StandardFonts } from "pdf-lib";
import { saveAs } from "file-saver";
import { QRCodeSVG } from "qrcode.react";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minHeight: "300px",
  },
  previewContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  qrPreview: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
    gap: theme.spacing(2),
    width: "100%",
    marginTop: theme.spacing(2),
  },
  qrItem: {
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  progress: {
    marginTop: theme.spacing(2),
  },
  statusText: {
    marginTop: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
}));

// QR code positions in the template based on the PDF structure
// Each position is [x, y, width, height] in PDF coordinates
// The positions are based on the XObject resources in the PDF
const QR_POSITIONS = [
  // First row (top) of QR codes
  [150, 1200, 180, 180], // Position 1
  [400, 1200, 180, 180], // Position 2
  [650, 1200, 180, 180], // Position 3

  // Second row of QR codes
  [150, 950, 180, 180], // Position 4
  [400, 950, 180, 180], // Position 5
  [650, 950, 180, 180], // Position 6

  // Third row of QR codes
  [150, 700, 180, 180], // Position 7
  [400, 700, 180, 180], // Position 8
  [650, 700, 180, 180], // Position 9

  // Fourth row of QR codes
  [150, 450, 180, 180], // Position 10
  [400, 450, 180, 180], // Position 11
  [650, 450, 180, 180], // Position 12

  // Fifth row of QR codes (if needed)
  [150, 200, 180, 180], // Position 13
  [400, 200, 180, 180], // Position 14
  [650, 200, 180, 180], // Position 15
];

// Number of QR codes per page - adjust based on the template
const QR_CODES_PER_PAGE = 15;

const PrintQRTickets = ({ open, onClose, ticketData }) => {
  const classes = useStyles();
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [previewTickets, setPreviewTickets] = useState([]);
  const [customTemplate, setCustomTemplate] = useState(null);
  const [useCustomTemplate, setUseCustomTemplate] = useState(false);
  const [customPositions, setCustomPositions] = useState(false);
  const [qrPositions, setQrPositions] = useState(QR_POSITIONS);
  const [qrCodesPerPage, setQrCodesPerPage] = useState(QR_CODES_PER_PAGE);

  useEffect(() => {
    // When the dialog opens, set up preview tickets
    if (open && ticketData && ticketData.individualTickets) {
      // Show first 6 tickets as preview
      setPreviewTickets(ticketData.individualTickets.slice(0, 6));
    }
  }, [open, ticketData]);

  // Handle template file upload
  const handleTemplateUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCustomTemplate(file);
      setUseCustomTemplate(true);
      setStatusMessage(`Custom template selected: ${file.name}`);
    }
  };

  // Function to load the PDF template with multiple fallback paths
  const loadPDFTemplate = async () => {
    // If using a custom template, read it directly
    if (useCustomTemplate && customTemplate) {
      try {
        setStatusMessage(`Loading custom template: ${customTemplate.name}`);
        return await readFileAsArrayBuffer(customTemplate);
      } catch (error) {
        console.error("Error loading custom template:", error);
        setStatusMessage(`Error loading custom template: ${error.message}`);
        // Fall back to default template paths
      }
    }

    const possiblePaths = [
      "/components/sports-tickets/qr_tickets_template.pdf",
      "/client/src/components/sports-tickets/qr_tickets_template.pdf",
      "/src/components/sports-tickets/qr_tickets_template.pdf",
      "/qr_tickets_template.pdf",
      // Add absolute path as a fallback
      `${window.location.origin}/qr_tickets_template.pdf`,
      // Add public folder path
      "/public/qr_tickets_template.pdf",
      // Add assets folder path
      "/assets/qr_tickets_template.pdf",
      // Try with different file name formats
      "/qr-tickets-template.pdf",
      "/qrTicketsTemplate.pdf",
    ];

    let lastError = null;
    let lastAttemptedPath = "";

    // Try each path until one works
    for (const path of possiblePaths) {
      try {
        lastAttemptedPath = path;
        setStatusMessage(`Trying to load template from: ${path}`);
        const response = await fetch(path);

        if (response.ok) {
          setStatusMessage(`Successfully loaded template from: ${path}`);
          return await response.arrayBuffer();
        } else {
          console.warn(
            `Failed to load PDF template from ${path}: HTTP ${response.status}`
          );
        }
      } catch (error) {
        lastError = error;
        console.warn(`Failed to load PDF template from ${path}:`, error);
      }
    }

    // If we get here, all paths failed
    const errorMessage = `Failed to load PDF template from any path. Last attempt: ${lastAttemptedPath}. Error: ${
      lastError ? lastError.message : "Unknown error"
    }`;
    console.error(errorMessage);

    // Show a more user-friendly error message
    setStatusMessage(
      "Could not load the PDF template. Please try uploading a custom template."
    );

    throw new Error(errorMessage);
  };

  // Helper function to read a file as ArrayBuffer
  const readFileAsArrayBuffer = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  // Handle position change
  const handlePositionChange = (index, axis, value) => {
    const newPositions = [...qrPositions];
    const axisIndex =
      axis === "x" ? 0 : axis === "y" ? 1 : axis === "width" ? 2 : 3;
    newPositions[index][axisIndex] = parseInt(value) || 0;
    setQrPositions(newPositions);
  };

  const generatePDF = async () => {
    if (
      !ticketData ||
      !ticketData.individualTickets ||
      !ticketData.individualTickets.length
    ) {
      setStatusMessage("No tickets available to print");
      return;
    }

    setIsGenerating(true);
    setProgress(0);
    setStatusMessage("Loading PDF template...");

    try {
      // Try to load the PDF template
      let pdfDoc;
      let useDefaultTemplate = true;

      try {
        // Load the PDF template using our robust loading function
        const templateArrayBuffer = await loadPDFTemplate();
        // Load the PDF template
        pdfDoc = await PDFDocument.load(templateArrayBuffer);
        useDefaultTemplate = false;
      } catch (error) {
        console.warn(
          "Failed to load template, creating a simple default template:",
          error
        );
        setStatusMessage("Creating a simple default template...");

        // Create a new PDF document as fallback
        pdfDoc = await PDFDocument.create();

        useDefaultTemplate = true;
      }

      // Embed a font that supports Cyrillic characters
      setStatusMessage("Loading fonts...");

      // Try to fetch and embed a font that supports Cyrillic
      let font;
      try {
        // Try to fetch Noto Sans font which has good Unicode support
        const fontUrl =
          "https://cdn.jsdelivr.net/npm/@fontsource/noto-sans/files/noto-sans-cyrillic-400-normal.woff";
        const fontResponse = await fetch(fontUrl);
        if (fontResponse.ok) {
          const fontData = await fontResponse.arrayBuffer();
          font = await pdfDoc.embedFont(fontData);
          console.log("Embedded custom font with Cyrillic support");
        } else {
          throw new Error("Could not load Noto Sans font");
        }
      } catch (fontError) {
        console.warn("Failed to load custom font:", fontError);

        try {
          // Fallback to Helvetica (which doesn't support Cyrillic, but we'll handle that)
          font = await pdfDoc.embedFont(StandardFonts.Helvetica);
          console.log("Using Helvetica as fallback font");
        } catch (fallbackError) {
          console.error("Failed to load fallback font:", fallbackError);
          // We'll handle text rendering carefully below
        }
      }

      // Function to safely draw text that might contain unsupported characters
      const safeDrawText = (page, text, options) => {
        try {
          // Try to draw the text with the font
          if (font) {
            page.drawText(text, { ...options, font });
          } else {
            page.drawText(text, options);
          }
        } catch (textError) {
          console.warn(`Could not draw text "${text}":`, textError);

          // Try to transliterate Cyrillic to Latin
          const transliterated = transliterateCyrillicToLatin(text);
          try {
            if (font) {
              page.drawText(transliterated, { ...options, font });
            } else {
              page.drawText(transliterated, options);
            }
          } catch (transError) {
            console.error(
              `Failed to draw transliterated text "${transliterated}":`,
              transError
            );
            // Last resort: just draw a placeholder
            try {
              page.drawText("[Text with unsupported characters]", options);
            } catch (e) {
              // Give up silently
            }
          }
        }
      };

      // Simple transliteration function for Cyrillic to Latin
      const transliterateCyrillicToLatin = (text) => {
        const cyrillicToLatin = {
          а: "a",
          б: "b",
          в: "v",
          г: "g",
          д: "d",
          е: "e",
          ё: "yo",
          ж: "zh",
          з: "z",
          и: "i",
          й: "y",
          к: "k",
          л: "l",
          м: "m",
          н: "n",
          о: "o",
          п: "p",
          р: "r",
          с: "s",
          т: "t",
          у: "u",
          ф: "f",
          х: "h",
          ц: "ts",
          ч: "ch",
          ш: "sh",
          щ: "sch",
          ъ: "",
          ы: "y",
          ь: "",
          э: "e",
          ю: "yu",
          я: "ya",
          А: "A",
          Б: "B",
          В: "V",
          Г: "G",
          Д: "D",
          Е: "E",
          Ё: "Yo",
          Ж: "Zh",
          З: "Z",
          И: "I",
          Й: "Y",
          К: "K",
          Л: "L",
          М: "M",
          Н: "N",
          О: "O",
          П: "P",
          Р: "R",
          С: "S",
          Т: "T",
          У: "U",
          Ф: "F",
          Х: "H",
          Ц: "Ts",
          Ч: "Ch",
          Ш: "Sh",
          Щ: "Sch",
          Ъ: "",
          Ы: "Y",
          Ь: "",
          Э: "E",
          Ю: "Yu",
          Я: "Ya",
        };

        return text
          .split("")
          .map((char) => cyrillicToLatin[char] || char)
          .join("");
      };

      // If using default template, create the first page
      if (useDefaultTemplate) {
        // Add a page with A4 dimensions
        const page = pdfDoc.addPage([595, 842]);

        // Add a title to the page
        const { width, height } = page.getSize();
        safeDrawText(page, "QR Tickets", {
          x: 50,
          y: height - 50,
          size: 30,
        });

        // Add event information
        if (ticketData && ticketData.sportsTicket) {
          const eventName = ticketData.sportsTicket.event_name || "";
          const homeTeam = ticketData.sportsTicket.home_team || "";
          const awayTeam = ticketData.sportsTicket.away_team || "";
          const eventDate = ticketData.sportsTicket.event_date
            ? new Date(ticketData.sportsTicket.event_date).toLocaleDateString()
            : "";

          safeDrawText(page, `Event: ${eventName}`, {
            x: 50,
            y: height - 90,
            size: 12,
          });

          safeDrawText(page, `Teams: ${homeTeam} vs ${awayTeam}`, {
            x: 50,
            y: height - 110,
            size: 12,
          });

          safeDrawText(page, `Date: ${eventDate}`, {
            x: 50,
            y: height - 130,
            size: 12,
          });
        }

        // Remove the page we just created since we'll clone it for each page
        const templatePage = pdfDoc.getPages()[0];
        pdfDoc.removePage(0);
      }

      const tickets = ticketData.individualTickets;
      const totalTickets = tickets.length;

      // Use custom or default QR codes per page
      const codesPerPage = customPositions ? qrCodesPerPage : QR_CODES_PER_PAGE;
      const totalPages = Math.ceil(totalTickets / codesPerPage);

      // Use custom or default QR positions
      let positions = customPositions ? qrPositions : QR_POSITIONS;

      // If using default template, use a simple grid layout
      if (useDefaultTemplate) {
        // Create positions for a simple grid layout (3 columns, 5 rows)
        positions = [];
        const pageWidth = 595;
        const pageHeight = 842;
        const margin = 50;
        const qrSize = 120;
        const cols = 3;
        const rows = 5;
        const colWidth = (pageWidth - 2 * margin) / cols;
        const rowHeight = (pageHeight - 2 * margin - 150) / rows; // 150px for header

        for (let row = 0; row < rows; row++) {
          for (let col = 0; col < cols; col++) {
            const x = margin + col * colWidth + (colWidth - qrSize) / 2;
            const y = pageHeight - margin - 150 - row * rowHeight - qrSize - 10;
            positions.push([x, y, qrSize, qrSize]);
          }
        }

        // Update codes per page
        codesPerPage = cols * rows;
      }

      setStatusMessage(
        `Preparing ${totalTickets} tickets across ${totalPages} pages...`
      );

      // Process tickets in batches to avoid memory issues
      const BATCH_SIZE = 50; // Process 50 tickets at a time
      let processedTickets = 0;

      // Create a copy of the first page to use as a template if not using default template
      let templatePage = null;
      if (!useDefaultTemplate && pdfDoc.getPageCount() > 0) {
        templatePage = pdfDoc.getPages()[0];
        // Remove the first page since we'll use it as a template to create new pages
        pdfDoc.removePage(0);
      }

      for (
        let batchStart = 0;
        batchStart < totalTickets;
        batchStart += BATCH_SIZE
      ) {
        const batchEnd = Math.min(batchStart + BATCH_SIZE, totalTickets);
        const batchTickets = tickets.slice(batchStart, batchEnd);

        // Process each ticket in the current batch
        for (let i = 0; i < batchTickets.length; i++) {
          const ticket = batchTickets[i];
          const ticketIndex = batchStart + i;
          const pageIndex = Math.floor(ticketIndex / codesPerPage);
          const positionIndex = ticketIndex % codesPerPage;

          // Create a new page if needed
          if (positionIndex === 0) {
            if (!useDefaultTemplate && templatePage) {
              // Clone the template page for each new page
              pdfDoc.addPage(templatePage.clone());
            } else if (useDefaultTemplate) {
              // Create a new page with A4 dimensions
              const page = pdfDoc.addPage([595, 842]);

              // Add a title to the page
              const { width, height } = page.getSize();
              safeDrawText(page, "QR Tickets", {
                x: 50,
                y: height - 50,
                size: 30,
              });

              // Add event information
              if (ticketData && ticketData.sportsTicket) {
                const eventName = ticketData.sportsTicket.event_name || "";
                const homeTeam = ticketData.sportsTicket.home_team || "";
                const awayTeam = ticketData.sportsTicket.away_team || "";
                const eventDate = ticketData.sportsTicket.event_date
                  ? new Date(
                      ticketData.sportsTicket.event_date
                    ).toLocaleDateString()
                  : "";

                safeDrawText(page, `Event: ${eventName}`, {
                  x: 50,
                  y: height - 90,
                  size: 12,
                });

                safeDrawText(page, `Teams: ${homeTeam} vs ${awayTeam}`, {
                  x: 50,
                  y: height - 110,
                  size: 12,
                });

                safeDrawText(page, `Date: ${eventDate}`, {
                  x: 50,
                  y: height - 130,
                  size: 12,
                });

                safeDrawText(page, `Page ${pageIndex + 1} of ${totalPages}`, {
                  x: width - 150,
                  y: height - 50,
                  size: 12,
                });
              }
            }
          }

          // Get the page to add the QR code to
          const page = pdfDoc.getPage(pageIndex);

          try {
            // Generate QR code as PNG
            const qrCodeDataUrl = await generateQRCodePNG(ticket.qr_code);
            const qrCodeImageBytes = await fetch(qrCodeDataUrl).then((res) =>
              res.arrayBuffer()
            );

            // Embed the QR code image in the PDF
            const qrCodeImage = await pdfDoc.embedPng(qrCodeImageBytes);

            // Get position for this QR code
            // Make sure we don't go out of bounds of our positions array
            const posIndex = Math.min(positionIndex, positions.length - 1);
            const [x, y, width, height] = positions[posIndex];

            // Draw the QR code on the page
            page.drawImage(qrCodeImage, {
              x,
              y,
              width,
              height,
            });

            // Add ticket information text below the QR code
            if (useDefaultTemplate) {
              safeDrawText(
                page,
                `Ticket #${ticket.ticket_number || ticketIndex + 1}`,
                {
                  x: x + width / 2 - 40,
                  y: y - 15,
                  size: 10,
                }
              );

              // Add QR code value in small text
              const qrValue = ticket.qr_code;
              if (qrValue && qrValue.length > 10) {
                const shortQrValue = qrValue.substring(0, 8) + "...";
                safeDrawText(page, shortQrValue, {
                  x: x + width / 2 - 20,
                  y: y - 30,
                  size: 8,
                });
              }
            }
          } catch (error) {
            console.error(`Error processing ticket ${ticketIndex}:`, error);
            // Continue with next ticket even if this one fails
          }

          processedTickets++;
          setProgress(Math.floor((processedTickets / totalTickets) * 100));

          // Update status message less frequently to reduce UI updates
          if (
            processedTickets % 10 === 0 ||
            processedTickets === totalTickets
          ) {
            setStatusMessage(
              `Processing ticket ${processedTickets} of ${totalTickets}...`
            );
          }
        }
      }

      setStatusMessage("Finalizing PDF...");

      // Save the PDF
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });

      // Generate filename based on event details
      const eventName = ticketData.sportsTicket.event_name.replace(/\s+/g, "_");
      const filename = `${eventName}_tickets.pdf`;

      // Download the PDF
      saveAs(blob, filename);

      setStatusMessage(
        `PDF generated successfully with ${totalTickets} tickets!`
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      setStatusMessage(`Error generating PDF: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  // Helper function to generate QR code as PNG
  const generateQRCodePNG = (qrData) => {
    return new Promise((resolve) => {
      // Create a temporary canvas to render the QR code
      const canvas = document.createElement("canvas");
      const size = 300; // High resolution for better quality
      canvas.width = size;
      canvas.height = size;

      try {
        // Create a temporary div to render the QR code
        const tempDiv = document.createElement("div");
        document.body.appendChild(tempDiv);

        // Render QR code to the temporary div
        const qrCodeElement = document.createElement("div");
        tempDiv.appendChild(qrCodeElement);

        // Use ReactDOM to render the QRCodeSVG component to the temporary div
        const ReactDOM = require("react-dom");

        // Check if createRoot is available (React 18+) or use legacy render
        if (ReactDOM.createRoot) {
          const root = ReactDOM.createRoot(qrCodeElement);
          root.render(
            <QRCodeSVG
              value={qrData}
              size={size}
              level="H"
              includeMargin={true}
            />
          );
        } else {
          ReactDOM.render(
            <QRCodeSVG
              value={qrData}
              size={size}
              level="H"
              includeMargin={true}
            />,
            qrCodeElement
          );
        }

        // Wait a bit for the render to complete
        setTimeout(() => {
          // Get the SVG element
          const svgElement = qrCodeElement.querySelector("svg");

          if (!svgElement) {
            throw new Error("SVG element not found after rendering QR code");
          }

          // Convert SVG to data URL
          const svgString = new XMLSerializer().serializeToString(svgElement);
          const svgBlob = new Blob([svgString], { type: "image/svg+xml" });
          const svgUrl = URL.createObjectURL(svgBlob);

          // Draw SVG on canvas
          const img = new Image();
          img.onload = () => {
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "white";
            ctx.fillRect(0, 0, size, size);
            ctx.drawImage(img, 0, 0, size, size);

            // Convert canvas to PNG data URL
            const pngDataUrl = canvas.toDataURL("image/png");

            // Clean up
            URL.revokeObjectURL(svgUrl);
            document.body.removeChild(tempDiv);

            resolve(pngDataUrl);
          };
          img.onerror = (error) => {
            console.error("Error loading SVG image:", error);
            // Fallback to a simple canvas with text
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "white";
            ctx.fillRect(0, 0, size, size);
            ctx.fillStyle = "black";
            ctx.font = "20px Arial";
            ctx.textAlign = "center";
            ctx.fillText(qrData, size / 2, size / 2);

            const pngDataUrl = canvas.toDataURL("image/png");
            document.body.removeChild(tempDiv);
            resolve(pngDataUrl);
          };
          img.src = svgUrl;
        }, 100);
      } catch (error) {
        console.error("Error generating QR code:", error);
        // Fallback to a simple canvas with text
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, size, size);
        ctx.fillStyle = "black";
        ctx.font = "20px Arial";
        ctx.textAlign = "center";
        ctx.fillText(qrData, size / 2, size / 2);

        const pngDataUrl = canvas.toDataURL("image/png");
        resolve(pngDataUrl);
      }
    });
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Print QR Tickets</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Typography variant="h6">
          {ticketData && ticketData.sportsTicket
            ? ticketData.sportsTicket.event_name
            : ""}
        </Typography>
        <Typography variant="body1">
          {ticketData && ticketData.sportsTicket
            ? ticketData.sportsTicket.home_team
            : ""}{" "}
          vs{" "}
          {ticketData && ticketData.sportsTicket
            ? ticketData.sportsTicket.away_team
            : ""}
        </Typography>
        <Typography variant="body1" gutterBottom>
          Total tickets:{" "}
          {ticketData && ticketData.individualTickets
            ? ticketData.individualTickets.length
            : 0}
        </Typography>

        <Divider className={classes.divider} />

        {/* Template Options */}
        <Typography variant="h6" gutterBottom>
          Template Options
        </Typography>

        <Typography variant="body2" color="textSecondary" paragraph>
          The system will try to load the default template. If it fails, a
          simple template will be generated automatically. You can also upload
          your own custom template.
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={useCustomTemplate}
                  onChange={(e) => setUseCustomTemplate(e.target.checked)}
                  color="primary"
                />
              }
              label="Use custom PDF template"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <input
              accept="application/pdf"
              style={{ display: "none" }}
              id="template-upload"
              type="file"
              onChange={handleTemplateUpload}
              disabled={!useCustomTemplate}
            />
            <label htmlFor="template-upload">
              <Button
                variant="contained"
                color="default"
                component="span"
                startIcon={<CloudUploadIcon />}
                disabled={!useCustomTemplate}
              >
                Upload Template
              </Button>
            </label>
            {customTemplate && (
              <Typography
                variant="caption"
                display="block"
                style={{ marginTop: "4px" }}
              >
                {customTemplate.name}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={customPositions}
                  onChange={(e) => setCustomPositions(e.target.checked)}
                  color="primary"
                />
              }
              label="Customize QR code positions"
            />
            <Tooltip title="Adjust the positions of QR codes on the PDF template. X and Y are coordinates from the bottom-left corner of the page.">
              <IconButton size="small">
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>

          {customPositions && (
            <Grid item xs={12}>
              <Paper style={{ padding: "16px", marginBottom: "16px" }}>
                <Typography variant="subtitle2" gutterBottom>
                  QR Codes Per Page:
                  <TextField
                    type="number"
                    value={qrCodesPerPage}
                    onChange={(e) =>
                      setQrCodesPerPage(
                        parseInt(e.target.value) || QR_CODES_PER_PAGE
                      )
                    }
                    style={{ marginLeft: "8px", width: "80px" }}
                    inputProps={{ min: 1, max: 30 }}
                    size="small"
                  />
                </Typography>

                <div
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    marginTop: "8px",
                  }}
                >
                  <Grid container spacing={1}>
                    {qrPositions
                      .slice(0, qrCodesPerPage)
                      .map((position, index) => (
                        <Grid item xs={12} key={index}>
                          <Typography variant="body2">
                            Position {index + 1}:
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={3}>
                              <TextField
                                label="X"
                                type="number"
                                value={position[0]}
                                onChange={(e) =>
                                  handlePositionChange(
                                    index,
                                    "x",
                                    e.target.value
                                  )
                                }
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <TextField
                                label="Y"
                                type="number"
                                value={position[1]}
                                onChange={(e) =>
                                  handlePositionChange(
                                    index,
                                    "y",
                                    e.target.value
                                  )
                                }
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <TextField
                                label="Width"
                                type="number"
                                value={position[2]}
                                onChange={(e) =>
                                  handlePositionChange(
                                    index,
                                    "width",
                                    e.target.value
                                  )
                                }
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <TextField
                                label="Height"
                                type="number"
                                value={position[3]}
                                onChange={(e) =>
                                  handlePositionChange(
                                    index,
                                    "height",
                                    e.target.value
                                  )
                                }
                                size="small"
                                fullWidth
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      ))}
                  </Grid>
                </div>
              </Paper>
            </Grid>
          )}
        </Grid>

        <Divider className={classes.divider} />

        <div className={classes.previewContainer}>
          <Typography variant="h6">Preview</Typography>
          <Typography variant="body2" color="textSecondary">
            The QR codes will be placed in the designated areas of the PDF
            template.
          </Typography>

          <Grid container className={classes.qrPreview}>
            {previewTickets.map((ticket) => (
              <Grid item key={ticket.id}>
                <Paper className={classes.qrItem}>
                  <QRCodeSVG
                    value={ticket.qr_code}
                    size={100}
                    level="H"
                    includeMargin={true}
                  />
                  <Typography variant="caption">
                    Ticket #{ticket.ticket_number}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </div>

        {isGenerating && (
          <div style={{ textAlign: "center" }}>
            <CircularProgress
              variant="determinate"
              value={progress}
              className={classes.progress}
            />
            <Typography className={classes.statusText}>
              {statusMessage} ({progress}%)
            </Typography>
          </div>
        )}

        {!isGenerating && statusMessage && (
          <Paper
            elevation={0}
            style={{
              backgroundColor:
                statusMessage.includes("Error") ||
                statusMessage.includes("Failed")
                  ? "#ffebee"
                  : "#e8f5e9",
              padding: "8px 16px",
              marginTop: "16px",
            }}
          >
            <Typography className={classes.statusText} color="textPrimary">
              {statusMessage}
            </Typography>
          </Paper>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
        <Button
          onClick={generatePDF}
          color="primary"
          variant="contained"
          disabled={
            isGenerating ||
            !(
              ticketData &&
              ticketData.individualTickets &&
              ticketData.individualTickets.length
            )
          }
        >
          {isGenerating ? "Generating..." : "Generate PDF"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintQRTickets;
